import React, { useMemo, useState } from "react";
import moment from "moment";
import {
  FaBan,
  FaChevronDown,
  FaChevronUp,
  FaEye,
  FaInbox,
  FaPlus,
  FaTrashAlt,
  FaUndo,
} from "react-icons/fa";
import ApplicationPaymentForm from "./ApplicationPaymentForm";
import FinanceInvoicePaymentModal from "./FinanceInvoicePaymentModal";
import FinanceInvoiceReversePaymentModal from "./FinanceInvoiceReversePaymentModal";
import { buildFinancePaymentLineRows } from "../../utilis/financeInvoicePaymentLineHelpers";
import { recordFinanceInvoicePaymentsSequentially } from "../../utilis/financeInvoicePaymentHelpers";
import {
  formatInvoiceLedgerTransactionAmount,
  getInvoiceLedgerTypeLabel,
  isLedgerDiscountReversed,
  isLedgerPaymentReversed,
  canReverseInvoiceLedgerTransaction,
  resolveTransactionAttachmentUrl,
  resolveTransactionAttachmentUrls,
} from "../../utilis/invoiceEditHelpers";

function getLedgerTypePillClass(type) {
  const normalized = String(type || "").toLowerCase();

  if (normalized === "payment") return "invoice-edit-pill--payment";
  if (normalized === "reversal" || normalized === "reverse_payment") {
    return "invoice-edit-pill--reversal";
  }
  if (normalized === "discount") return "invoice-edit-pill--discount";
  if (normalized === "discount_reversal") return "invoice-edit-pill--discount-reversal";

  return "invoice-edit-pill--neutral";
}

function getLedgerAmountClassName(type) {
  const normalized = String(type || "").toLowerCase();

  if (normalized === "payment" || normalized === "reversal" || normalized === "reverse_payment") {
    return "invoice-edit-table__amount--payment";
  }

  if (normalized === "discount" || normalized === "discount_reversal") {
    return "invoice-edit-table__amount--discount";
  }

  return "";
}

function InvoiceEditPaymentsPanel({
  currency = "AUD",
  invoiceNumber = "",
  transactions = [],
  applicationInvoices = [],
  ledgerEntries = [],
  paymentForm = null,
  allowAddPayment = false,
  onSaveFinanceInvoicePayment = null,
  onSaveFinanceInvoiceBulkPayment = null,
  savingFinanceInvoicePayment = false,
  onReverseFinanceInvoicePayment = null,
  reversingFinanceInvoicePayment = false,
  onRemoveDraftPayment = null,
  amountDue = 0,
  lineItems = [],
  draftPayments = [],
  onReversePayment = null,
  reversingPayment = false,
  readOnly = false,
}) {
  const [sectionExpanded, setSectionExpanded] = useState(true);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [showFinancePaymentModal, setShowFinancePaymentModal] = useState(false);
  const [showReversePaymentModal, setShowReversePaymentModal] = useState(false);
  const [selectedPaymentRecord, setSelectedPaymentRecord] = useState(null);
  const [reversingPaymentId, setReversingPaymentId] = useState(null);
  /** Locked when payment modal opens — bulk handler must not drop mid-save. */
  const [financePaymentModalBulkMode, setFinancePaymentModalBulkMode] =
    useState(false);

  const useFinancePaymentModal = Boolean(onSaveFinanceInvoicePayment);
  const useFinanceReverseModal = Boolean(onReverseFinanceInvoicePayment);
  const allowRemoveDraft = Boolean(onRemoveDraftPayment);
  const reversalEntries = ledgerEntries.length ? ledgerEntries : transactions;

  const payableLineRows = useMemo(
    () =>
      buildFinancePaymentLineRows({
        lineItems,
        draftPayments,
        existingTransactions: transactions,
      }),
    [lineItems, draftPayments, transactions],
  );

  const useBulkLinePaymentModal = payableLineRows.length > 1;
  const useBulkPaymentSave =
    financePaymentModalBulkMode || useBulkLinePaymentModal;

  const handleBulkFinancePaymentSave = async (entries) => {
    if (onSaveFinanceInvoiceBulkPayment) {
      return onSaveFinanceInvoiceBulkPayment(entries);
    }
    if (!onSaveFinanceInvoicePayment) return false;
    return recordFinanceInvoicePaymentsSequentially(
      entries,
      onSaveFinanceInvoicePayment,
    );
  };

  const openFinancePaymentModal = () => {
    setSectionExpanded(true);
    setFinancePaymentModalBulkMode(useBulkLinePaymentModal);
    setShowFinancePaymentModal(true);
  };

  const closeFinancePaymentModal = () => {
    setShowFinancePaymentModal(false);
    setFinancePaymentModalBulkMode(false);
  };

  const handleSubmit = async () => {
    const success = await paymentForm?.onSubmit?.();
    if (success) setShowPaymentForm(false);
  };

  const handleCancel = () => {
    paymentForm?.onCancel?.();
    setShowPaymentForm(false);
  };

  const handleReversePayment = async (record) => {
    if (reversingPayment || reversingPaymentId) return;

    if (useFinanceReverseModal) {
      setSelectedPaymentRecord(record);
      setShowReversePaymentModal(true);
      return;
    }

    if (!onReversePayment) return;

    setReversingPaymentId(record.id);
    try {
      await onReversePayment(record);
    } finally {
      setReversingPaymentId(null);
    }
  };

  return (
    <section
      className={`invoice-edit-section invoice-edit-transactions${sectionExpanded ? " invoice-edit-section--expanded" : ""
        }`}
    >
      <div className="invoice-edit-section__header">
        <button
          type="button"
          className="invoice-edit-section__toggle"
          onClick={() => setSectionExpanded((open) => !open)}
          aria-expanded={sectionExpanded}
        >
          {sectionExpanded ? (
            <FaChevronUp size={11} />
          ) : (
            <FaChevronDown size={11} />
          )}
          <h3 className="invoice-edit-section__title mb-0">Transactions</h3>
          <span className="invoice-edit-count-badge">{transactions.length}</span>
        </button>

        {allowAddPayment && !readOnly &&
          !showPaymentForm &&
          !(useFinancePaymentModal && showFinancePaymentModal) && (
            <button
              type="button"
              className="btn btn-sm btn-outline-primary invoice-edit-section__action"
              onClick={() => {
                if (useFinancePaymentModal) {
                  openFinancePaymentModal();
                } else {
                  setSectionExpanded(true);
                  setShowPaymentForm(true);
                }
              }}
              disabled={Number(amountDue) <= 0}
            >
              <FaPlus size={11} className="me-1" />
              Add payment
            </button>
          )}
      </div>

      {sectionExpanded && (
        <div className="invoice-edit-section__body">
          {paymentForm && showPaymentForm && (
            <div className="invoice-edit-transactions__form-card">
              <ApplicationPaymentForm
                compact
                title="Add Payment"
                uploadInputId="invoiceEditPaymentReceipt"
                payment={paymentForm.payment}
                onPaymentChange={paymentForm.onPaymentChange}
                receiptFile={paymentForm.receiptFile}
                receiptPreview={paymentForm.receiptPreview}
                onReceiptChange={paymentForm.onReceiptChange}
                onReceiptRemove={paymentForm.onReceiptRemove}
                onDrop={paymentForm.onDrop}
                onDragOver={paymentForm.onDragOver}
                creditsAvailable={paymentForm.creditsAvailable}
                remainingBalance={paymentForm.remainingBalance}
                loading={paymentForm.loading}
                onSubmit={handleSubmit}
                onCancel={handleCancel}
              />
            </div>
          )}

          <div className="invoice-edit-transactions__table-wrap">
            {transactions.length > 0 ? (
              <div className="invoice-edit-table-wrap">
                <table className="invoice-edit-table invoice-edit-table--transactions">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Type</th>
                      <th className="text-end">Amount</th>
                      <th>Date</th>
                      <th>Description</th>
                      <th>Recorded by</th>
                      <th className="invoice-edit-table__actions-col text-end">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((record) => {
                      const type = String(record?.type || "").toLowerCase();
                      const isPayment = type === "payment";
                      const isDiscount = type === "discount";
                      const isReversalEntry =
                        type === "reversal" ||
                        type === "reverse_payment" ||
                        type === "discount_reversal";
                      const isPaymentReversed = isLedgerPaymentReversed(
                        record,
                        reversalEntries,
                      );
                      const isDiscountReversed = isLedgerDiscountReversed(
                        record,
                        reversalEntries,
                      );
                      const isReversingPayment =
                        reversingPayment ||
                        reversingFinanceInvoicePayment ||
                        reversingPaymentId === record.id;
                      const attachmentUrls = resolveTransactionAttachmentUrls(record);
                      const isDraftPayment =
                        allowRemoveDraft &&
                        isPayment &&
                        String(record?.id || "").startsWith("draft-");

                      return (
                        <tr key={record.id}>
                          <td className="invoice-edit-table__id">{record.id}</td>
                          <td>
                            <span
                              className={`invoice-edit-pill ${getLedgerTypePillClass(type)}`}
                            >
                              {getInvoiceLedgerTypeLabel(type)}
                            </span>
                          </td>
                          <td
                            className={`text-end invoice-edit-table__amount ${getLedgerAmountClassName(type)}`}
                          >
                            {formatInvoiceLedgerTransactionAmount(
                              currency,
                              record.amount,
                              type,
                            )}
                          </td>
                          <td className="invoice-edit-table__date">
                            {record.transaction_date
                              ? moment(record.transaction_date).format(
                                "D MMM YYYY",
                              )
                              : "—"}
                          </td>
                          <td className="invoice-edit-table__description">
                            <span>{record.description || "—"}</span>
                          </td>
                          <td className="invoice-edit-table__muted">
                            {record.user?.name ||
                              record.creator?.name ||
                              record.created_by_name ||
                              "—"}
                          </td>
                          <td className="invoice-edit-table__actions text-end">
                            {isDraftPayment ? (
                              <div className="invoice-edit-table__action-group">
                                {attachmentUrls.map((attachmentUrl, attachmentIndex) => (
                                  <button
                                    key={`${record.id}-attachment-${attachmentIndex}`}
                                    type="button"
                                    className="invoice-edit-icon-btn"
                                    onClick={() =>
                                      window.open(attachmentUrl, "_blank")
                                    }
                                    title={`View attachment ${attachmentIndex + 1}`}
                                    aria-label={`View attachment ${attachmentIndex + 1}`}
                                  >
                                    <FaEye size={12} />
                                  </button>
                                ))}
                                <button
                                  type="button"
                                  className="invoice-edit-icon-btn invoice-edit-icon-btn--danger"
                                  onClick={() => onRemoveDraftPayment?.(record)}
                                  title="Remove payment"
                                  aria-label="Remove payment"
                                >
                                  <FaTrashAlt size={12} />
                                </button>
                              </div>
                            ) : isReversalEntry ? (
                              <span
                                className={`invoice-edit-pill invoice-edit-pill--status ${type === "discount_reversal"
                                    ? "invoice-edit-pill--discount-reversal-status"
                                    : "invoice-edit-pill--reversal-status"
                                  }`}
                                title={
                                  record.reverses_entry_id
                                    ? `Reverses ID: ${record.reverses_entry_id}`
                                    : type === "reverse_payment"
                                      ? "Reverse payment"
                                      : "Reversal"
                                }
                              >
                                <FaUndo size={9} className="me-1" />
                                {type === "discount_reversal"
                                  ? "Rev: Discount"
                                  : type === "reverse_payment"
                                    ? "Rev: Payment"
                                    : "Rev: Payment"}
                              </span>
                            ) : isPayment && isPaymentReversed ? (
                              <span
                                className="invoice-edit-pill invoice-edit-pill--status invoice-edit-pill--reversed"
                                title="This payment has been reversed"
                              >
                                <FaBan size={9} className="me-1" />
                                Reversed
                              </span>
                            ) : isDiscount && isDiscountReversed ? (
                              <span
                                className="invoice-edit-pill invoice-edit-pill--status invoice-edit-pill--reversed"
                                title="This discount has been reversed"
                              >
                                <FaBan size={9} className="me-1" />
                                Reversed
                              </span>
                            ) : isPayment && !readOnly ? (
                              <div className="invoice-edit-table__action-group">
                                <button
                                  type="button"
                                  className="invoice-edit-icon-btn invoice-edit-icon-btn--danger"
                                  onClick={() => handleReversePayment(record)}
                                  disabled={
                                    isReversingPayment ||
                                    (!onReverseFinanceInvoicePayment &&
                                      !onReversePayment) ||
                                    !canReverseInvoiceLedgerTransaction(
                                      record,
                                      reversalEntries,
                                    )
                                  }
                                  title="Reverse payment"
                                  aria-label="Reverse payment"
                                >
                                  <FaUndo size={12} />
                                </button>
                                {attachmentUrls.map((attachmentUrl, attachmentIndex) => (
                                  <button
                                    key={`${record.id}-attachment-${attachmentIndex}`}
                                    type="button"
                                    className="invoice-edit-icon-btn"
                                    onClick={() =>
                                      window.open(attachmentUrl, "_blank")
                                    }
                                    title={`View attachment ${attachmentIndex + 1}`}
                                    aria-label={`View attachment ${attachmentIndex + 1}`}
                                  >
                                    <FaEye size={12} />
                                  </button>
                                ))}
                              </div>
                            ) : isDiscount ? (
                              attachmentUrls.length ? (
                                attachmentUrls.map((attachmentUrl, attachmentIndex) => (
                                  <button
                                    key={`${record.id}-attachment-${attachmentIndex}`}
                                    type="button"
                                    className="invoice-edit-icon-btn"
                                    onClick={() =>
                                      window.open(attachmentUrl, "_blank")
                                    }
                                    title={`View attachment ${attachmentIndex + 1}`}
                                    aria-label={`View attachment ${attachmentIndex + 1}`}
                                  >
                                    <FaEye size={12} />
                                  </button>
                                ))
                              ) : (
                                <span className="invoice-edit-table__muted">—</span>
                              )
                            ) : attachmentUrls.length ? (
                              attachmentUrls.map((attachmentUrl, attachmentIndex) => (
                                <button
                                  key={`${record.id}-attachment-${attachmentIndex}`}
                                  type="button"
                                  className="invoice-edit-icon-btn"
                                  onClick={() =>
                                    window.open(attachmentUrl, "_blank")
                                  }
                                  title={`View attachment ${attachmentIndex + 1}`}
                                  aria-label={`View attachment ${attachmentIndex + 1}`}
                                >
                                  <FaEye size={12} />
                                </button>
                              ))
                            ) : (
                              "—"
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="invoice-edit-empty invoice-edit-empty--section">
                <FaInbox
                  className="invoice-edit-empty__icon"
                  aria-hidden="true"
                />
                <p className="invoice-edit-empty__text">
                  No transactions recorded for this invoice yet.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      <FinanceInvoicePaymentModal
        show={showFinancePaymentModal}
        onClose={closeFinancePaymentModal}
        onSave={onSaveFinanceInvoicePayment}
        onSaveBulk={
          useBulkPaymentSave ? handleBulkFinancePaymentSave : null
        }
        saving={savingFinanceInvoicePayment}
        forceLineAllocation={financePaymentModalBulkMode}
        currency={currency}
        amountDue={amountDue}
        invoiceNumber={invoiceNumber}
        lineItems={lineItems}
        draftPayments={draftPayments}
        existingTransactions={transactions}
      />

      <FinanceInvoiceReversePaymentModal
        show={showReversePaymentModal}
        onClose={() => {
          setShowReversePaymentModal(false);
          setSelectedPaymentRecord(null);
        }}
        onSave={onReverseFinanceInvoicePayment}
        saving={reversingFinanceInvoicePayment}
        currency={currency}
        invoiceNumber={invoiceNumber}
        paymentRecord={selectedPaymentRecord}
      />
    </section>
  );
}

export default InvoiceEditPaymentsPanel;
